import { useContext } from 'react'

import { SortContext } from 'context/sortContext'
import {
  Account,
  AccountActivity,
  Activity,
  ActivityContext,
  ActivityEndUser,
  ActivityForContact,
  CompanyData
} from 'modules/accountActivity/domain'
import { FilterPage } from 'utils/filters'

import { useInsightsFeed } from './useInsightsFeed'

interface DashboardInsight {
  accountId: string
  activityCounts: Record<string, number>
  lastActivityAt: number
  accountData: Account
  companyData: CompanyData
  scoredAccount: AccountActivity
  id: number | string
  lastActivityEvent: ActivityForContact & Activity & ActivityContext
  endUserData: ActivityEndUser
}

const useDashboardInsights = (): {
  insights: DashboardInsight[]
  hasLoadedInitialData: boolean
  isRefreshing: boolean
  error: Error
  refresh: () => void
} => {
  const { activitiesSort, activitiesDescending } = useContext(SortContext)

  const insightsFeed = useInsightsFeed(
    FilterPage.ACTIVITIES,
    activitiesSort,
    activitiesDescending,
    3,
    true
  )

  const { refresh } = insightsFeed

  const { data: insightsData, hasLoadedInitialData, isLoadingMore, error } = insightsFeed.insights

  const dashboardInsightsCount = insightsData.reduce((acc: any[], insight: any) => {
    const { body, accountData, lastActivityAt, companyData, scoredAccount, id } = insight
    const { activityCounts, activityGroupsByEndUser } = body
    const lastActivityEvent =
      activityGroupsByEndUser?.find(
        (activity: any) =>
          activity.lastActivityAt === lastActivityAt || activity.createdAt === lastActivityAt
      ) || activityGroupsByEndUser?.[0]

    // FILTER OUT INSIGHTS THAT DON'T HAVE ACTIVITY GROUPS BY END USER
    if (!lastActivityEvent) {
      return acc
    }

    return [
      ...acc,
      {
        accountData,
        activityCounts,
        lastActivityAt,
        companyData,
        scoredAccount: { ...scoredAccount, companyData },
        id,
        lastActivityEvent: lastActivityEvent?.activityList[0],
        endUserData: lastActivityEvent?.endUserData
      }
    ]
  }, [])

  return {
    insights: dashboardInsightsCount,
    isRefreshing: isLoadingMore,
    hasLoadedInitialData,
    error,
    refresh
  }
}

export default useDashboardInsights
